import { Check, ShieldCheck, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { conversionModalCta } from "../../lib/funnel";
import { campaignOffer } from "../../lib/plans";
import { OFFER_CHIP } from "./plus-slots";
import { trackModalClick, trackModalView } from "../../lib/tracking";
import type { FunnelCta } from "../../lib/tracking";
import {
  CONVERSION_GUARANTEE,
  CONVERSION_PROOF,
  conversionPlans,
  conversionTitle,
  DEFAULT_PLAN,
  FREE_LIMITS,
  PLAN_ORDER,
  PLUS_BENEFITS,
  perDay,
  type PlanSlug,
} from "./conversion-modal-copy";

/**
 * A modal que entra entre os banners e o checkout.
 *
 * Os dez formatos desta pasta levavam direto ao /checkout/. Esta modal
 * reconhece o limite, mostra o PREÇO — que não existia em lado nenhum dentro do
 * Lume — e põe o grátis ao lado do pago, para a escolha ser uma comparação e
 * não um salto. Sai daqui para o checkout com o plano escolhido no ?plan=.
 *
 * NÃO TEM LOCATION PRÓPRIA. É aberta de sete sítios e herda a location e o type
 * de quem a abriu; só o element é seu (`conversion_modal`) — ver
 * lib/funnel.conversionModalCta.
 *
 * NO TESTE DOS BANNERS SÓ ENTRA O TÍTULO (conversionTitle). O resto é igual
 * nos dois braços, para a diferença lida no teste ser a da copy.
 *
 * Num telemóvel o botão vem logo a seguir ao preço e os benefícios descem para
 * baixo dele: depois dos cinco benefícios cai fora do primeiro ecrã.
 *
 * Desenho: docs/modal-conversao-tmplus-lume.html, com uma divergência assumida
 * no estreito: o mockup põe os dois cartões num carrossel com pontos e AQUI O
 * CARTÃO GRÁTIS NÃO APARECE DE TODO. Num ecrã de telemóvel os dois cartões não
 * cabem lado a lado, e um cartão "Continuar no grátis" empilhado por baixo do
 * pago é a última coisa que o aluno lê antes de decidir — a comparação que no
 * desktop informa a escolha passa a ser um convite a desistir dela. A saída
 * para o grátis continua a existir: é o ✕, que fecha a modal e devolve o aluno
 * ao sítio onde estava. A tooltip da prova social também não veio.
 */
export default function PlusConversionModal({
  onClose,
  origin,
  scope = "",
}: {
  onClose: () => void;
  /** O CTA do banner que abriu a modal, de lib/funnel. */
  origin: FunnelCta;
  /** A classe de tema da página — a modal é portalada por cima dela. */
  scope?: string;
}) {
  const [slug, setSlug] = useState<PlanSlug>(DEFAULT_PLAN);
  const [noteOpen, setNoteOpen] = useState(false);
  const [card, setCard] = useState(0);
  const railRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const noteRef = useRef<HTMLDivElement>(null);
  const noteButtonRef = useRef<HTMLButtonElement>(null);
  const cta = conversionModalCta(origin);
  // Resolvidos uma vez por abertura: o componente remonta a cada abertura, por
  // isso isto não congela uma campanha que entretanto tenha acabado.
  const plans = useMemo(() => conversionPlans(), []);
  const campaign = useMemo(() => campaignOffer(), []);
  const title = conversionTitle();
  const plan = plans[slug];

  /**
   * A impressão conta na ABERTURA, não no mount da tela que a hospeda — é o que
   * distingue "viu a modal" de "esteve numa página que a poderia abrir".
   * Depende só de `cta`, que é memoizado por origem: trocar de plano dentro da
   * modal não é uma segunda impressão.
   */
  useEffect(() => trackModalView(cta), [cta]);

  /**
   * O foco inicial, uma vez por abertura e MAIS NADA.
   *
   * Sozinho no seu effect porque o de baixo depende do `noteOpen`: enquanto
   * esta linha lá viveu, abrir a nota ⓘ roubava o foco para o ✕, e quem
   * navegava por teclado carregava em Espaço uma segunda vez a pensar que
   * ainda estava na nota e fechava a modal inteira.
   */
  useEffect(() => {
    closeRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    /**
     * Escape fecha primeiro a nota e só depois a modal. Ao contrário, quem
     * abriu a nota e carregou em Esc para a fechar perdia a modal inteira.
     */
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "Escape") return;

      if (noteOpen) {
        setNoteOpen(false);
        noteButtonRef.current?.focus({ preventScroll: true });
        return;
      }

      onClose();
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [noteOpen, onClose]);

  /**
   * Um clique fora fecha a nota.
   *
   * composedPath() E NÃO event.target: o Lume vive numa shadow root aberta e o
   * alvo de um clique lá dentro é re-apontado para o host na saída, por isso
   * um `contains(event.target)` daria sempre falso e a nota fechava-se ao ser
   * aberta. O caminho composto atravessa a fronteira.
   */
  useEffect(() => {
    if (!noteOpen) return;

    function onDocumentClick(event: MouseEvent) {
      const path = event.composedPath();

      if (
        (noteRef.current && path.includes(noteRef.current)) ||
        (noteButtonRef.current && path.includes(noteButtonRef.current))
      ) {
        return;
      }

      setNoteOpen(false);
    }

    document.addEventListener("click", onDocumentClick);

    return () => document.removeEventListener("click", onDocumentClick);
  }, [noteOpen]);

  /**
   * O link real para o checkout. `referrer` diz de onde veio a conversão para
   * lá do último toque que o purchase do servidor lê, e o ?plan= é o pedido:
   * leva escolhido o plano que estiver aberto.
   */
  const href = `/checkout/?plan=${plan.slug}&referrer=${encodeURIComponent(
    `${origin.location}__conversion_modal`
  )}`;

  return (
    <div
      aria-label="Assinar o Toda Matéria PLUS"
      aria-modal="true"
      className={`${scope} fixed inset-0 z-100 flex items-end justify-center overflow-hidden p-0 max-sm:[@media(min-height:900px)]:pt-10 sm:items-center sm:p-6`}
      role="dialog"
    >
      <button
        aria-label="Fechar"
        className="fixed inset-0 cursor-default bg-[rgba(17,24,39,0.65)] backdrop-blur-[4px]"
        onClick={onClose}
        tabIndex={-1}
        type="button"
      />

      {/* NUNCA MAIS ALTA DO QUE O ECRÃ, e é o CONTEÚDO que rola, não a modal.
          Com a modal inteira a rolar dentro do wrapper, um cartão mais alto do
          que o viewport ficava com o topo cortado — é o comportamento do flex
          com items-end/center — e com ele ia o ✕, que é a única saída num
          telemóvel. Assim a moldura fica quieta, o botão de fechar fica sempre
          onde está, e só a coluna por baixo dele se move.

          max-h-full e não uma altura em dvh: o wrapper é inset-0 com padding,
          por isso "cheio" já desconta a margem que deixa o fundo visível — e o
          fundo visível é o segundo modo de fechar, porque é clicável.

          Num telemóvel é ecrã inteiro: a bottom sheet transbordava e escondia
          os planos. Só os muito altos (≥ 900px) mantêm a sheet. */}
      <div className="animate-rise relative flex max-h-full w-full h-full max-w-[1020px] flex-col overflow-hidden rounded-none border-0 border-[rgba(155,160,255,0.26)] max-sm:[@media(min-height:900px)]:h-auto max-sm:[@media(min-height:900px)]:rounded-t-[26px] max-sm:[@media(min-height:900px)]:border sm:h-auto sm:border bg-[radial-gradient(800px_480px_at_18%_-10%,rgba(106,112,242,0.4),transparent_68%)] bg-[#14122b] text-[#f4f4ff] shadow-[0_40px_100px_rgba(0,0,0,0.6)] sm:rounded-[26px]">
        {/* o brilho coral do canto, decorativo */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -bottom-55 size-130 rounded-full bg-[radial-gradient(circle,rgba(255,122,92,0.14),transparent_70%)]"
        />

        <button
          aria-label="Fechar"
          className="absolute top-[max(0.875rem,env(safe-area-inset-top))] right-3.5 z-10 grid size-11 cursor-pointer place-items-center rounded-full border border-[rgba(155,160,255,0.28)] bg-white/8 text-[#f4f4ff] transition hover:bg-white/16"
          onClick={onClose}
          ref={closeRef}
          type="button"
        >
          <X aria-hidden="true" className="size-3.5" />
        </button>

        {/* min-h-0 é o que deixa este filho encolher abaixo do seu conteúdo e
            portanto rolar; sem ele o flex dá-lhe a altura toda e a barra nunca
            aparece. overscroll-contain para o gesto não passar para a página
            por baixo quando chega ao fim. O padding de baixo respeita a barra
            do iOS, senão o CTA fica debaixo dela. */}
        <div className="relative z-2 flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto overscroll-contain px-5 pt-[calc(3.5rem+env(safe-area-inset-top))] pb-[max(1.75rem,env(safe-area-inset-bottom))] sm:px-10 sm:pt-9 sm:pb-7.5">
          <header className="flex flex-col items-center gap-3.5 text-center">
            {campaign !== null && (
              <span className={OFFER_CHIP}>
                {campaign.percentOff}% OFF na reta final
              </span>
            )}

            <h2 className="m-0 max-w-[24ch] text-[26px]/[1.05] font-black tracking-[-0.035em] text-balance sm:text-[38px]">
              {title.before}
              {/* O gradiente da marca no nome. forced-colors devolve-lhe uma cor
                  a sério: sem isso o texto recortado desaparece e o título
                  passa a ler-se "O sem limites". */}
              {title.word !== "" && (
                <span className="bg-clip-text font-black text-transparent [background-image:radial-gradient(125%_135%_at_28%_24%,#9ba0ff_0%,#6a70f2_42%,#ff7a5c_100%)] forced-colors:bg-none forced-colors:text-[CanvasText]">
                  {title.word}
                </span>
              )}
              {title.after}
            </h2>

            {/* Os planos. Um radiogroup e não três botões soltos: é uma escolha
                entre três, e o teclado tem de a tratar como tal. */}
            <div
              aria-label="Escolher plano"
              className="inline-flex gap-0.75 rounded-full border border-[rgba(155,160,255,0.3)] bg-white/7 p-0.75"
              role="tablist"
            >
              {PLAN_ORDER.map((key) => {
                const option = plans[key];
                const selected = key === slug;

                return (
                  <button
                    aria-selected={selected}
                    className={`relative inline-flex cursor-pointer items-center gap-1.75 rounded-full border-0 px-3.25 py-2 text-[13px] font-bold whitespace-nowrap transition sm:px-4 ${
                      selected
                        ? "bg-[#f4f4ff] text-[#14122b]"
                        : "bg-transparent text-white/62 hover:text-[#f4f4ff]"
                    }`}
                    key={key}
                    onClick={() => setSlug(key)}
                    role="tab"
                    type="button"
                  >
                    {option.tab}
                    {option.tabOff ? (
                      <span
                        className={`hidden rounded-full px-1.5 py-0.5 font-mono text-[10px] font-extrabold tracking-[0.05em] sm:inline ${
                          selected
                            ? "bg-[#3e45eb] text-white"
                            : "bg-[rgba(155,160,255,0.2)] text-[#b9bdff]"
                        }`}
                      >
                        {option.tabOff}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </header>

          {/* ── Estreito: carrossel. Largo: as duas colunas a par. ──
              No telemóvel os dois cartões não cabem lado a lado e empilhá-los
              punha o "Continuar no grátis" a fechar a página — a última coisa
              lida antes de decidir. Deslizam-se, com o pago à partida e o
              grátis a um gesto à direita, que é o que o mockup faz.

              As margens negativas levam o carril até à beira da modal para o
              snap centrar os cartões; o padding devolve o ar.

              O PADDING VERTICAL NÃO É DECORATIVO. overflow-x:auto faz o
              overflow-y calcular-se como auto, e a caixa passa a cortar tudo o
              que sai dela — o selo do plano, que vive 12px acima do cartão, e o
              halo do cartão pago. As 26px de padding (contra -20px de margem)
              são o espaço em que eles cabem dentro do carril. */}
          <div
            className="-mx-5 -my-5 flex shrink-0 snap-x snap-mandatory items-stretch gap-2 overflow-x-auto overflow-y-hidden overscroll-x-contain px-5 py-6.5 [scrollbar-width:none] lg:m-0 lg:grid lg:snap-none lg:grid-cols-[1fr_1.14fr] lg:items-start lg:gap-4.5 lg:overflow-visible lg:p-0 [&::-webkit-scrollbar]:hidden"
            onScroll={(event) => {
              const rail = event.currentTarget;
              const next = Math.round(rail.scrollLeft / rail.clientWidth);

              if (next !== card) setCard(next);
            }}
            ref={railRef}
          >
            <section className="order-2 flex w-full flex-none snap-center flex-col gap-4 rounded-[20px] border border-[rgba(155,160,255,0.22)] bg-white/5 p-6 lg:order-1 lg:w-auto lg:flex-auto">
              <div>
                <h3 className="m-0 text-[19px] font-extrabold">Plano grátis</h3>
                <p className="mt-1.25 mb-0 text-[13.5px]/[1.45] font-medium text-white/62">
                  O que você já tem hoje, e até onde vai.
                </p>
              </div>

              <ul className="m-0 flex list-none flex-col gap-2.75 p-0">
                {FREE_LIMITS.map((row) => (
                  <li
                    className="flex items-start gap-2.75 text-[14px]/[1.45] font-medium text-white/80"
                    key={row.label}
                  >
                    <span
                      aria-hidden="true"
                      className="mt-0.25 grid size-4.75 flex-none place-items-center rounded-full bg-white/12 text-[12px]/none font-extrabold text-white/62"
                    >
                      ·
                    </span>
                    <span>
                      <b className="font-extrabold text-[#f4f4ff]">
                        {row.label}
                      </b>{" "}
                      — {row.limit}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                className="mt-auto flex min-h-11 w-full cursor-pointer items-center justify-center rounded-[12px] border border-white/40 bg-transparent px-6 py-4 text-[15px] font-extrabold text-[#f4f4ff]"
                onClick={onClose}
                type="button"
              >
                Continuar no grátis
              </button>
            </section>

            <section className="relative order-1 w-full flex-none snap-center rounded-[22px] bg-[linear-gradient(150deg,#9ba0ff,#6a70f2_55%,#3e45eb)] p-0.5 shadow-[0_0_20px_rgba(106,112,242,0.4)] lg:order-2 lg:w-auto lg:flex-auto lg:shadow-[0_0_90px_rgba(106,112,242,0.5)]">
              {/* max-w para o selo nunca ser mais largo do que o cartão: as
                  legendas com desconto ("25% OFF · COBRE ATÉ O ENEM") são quase
                  o dobro das outras, e num telemóvel é onde a diferença conta. */}
              <span className="absolute -top-3 left-1/2 z-3 max-w-[calc(100%-1rem)] -translate-x-1/2 overflow-hidden rounded-full bg-[#3e45eb] px-2.5 py-1.25 font-mono text-[9.5px] font-extrabold tracking-[0.08em] text-ellipsis whitespace-nowrap text-white uppercase sm:px-3.5 sm:text-[10.5px] sm:tracking-[0.1em]">
                {plan.badge}
              </span>

              <div className="flex flex-col gap-3.5 rounded-[20px] bg-[radial-gradient(600px_300px_at_20%_0%,rgba(106,112,242,0.4),transparent_70%)] bg-[#191636] p-6">
                <div className="flex flex-wrap items-center gap-2.5">
                  <b className="text-[23px] font-extrabold tracking-[-0.02em]">
                    {plan.card}
                  </b>
                </div>

                {/* OS TRÊS PREÇOS NA MESMA CÉLULA, dois deles invisíveis.
                    A validade é a mesma frase com um número diferente, e
                    "Acesso de 12 meses." é a única que passa para mais uma
                    linha — por isso escolher o anual crescia o cartão uns
                    pixéis e, com items-center, a modal inteira re-centrava-se.
                    Empilhados numa grelha, a célula fica com a altura do mais
                    alto e trocar de plano deixa de mexer o que quer que seja.
                    Sem número mágico: é o conteúdo real que a mede. */}
                <div className="grid border-y border-[rgba(155,160,255,0.2)] py-3.5">
                  {PLAN_ORDER.map((key) => (
                    <div
                      aria-hidden={key !== slug}
                      className={`col-start-1 row-start-1 flex flex-col gap-1.5 ${
                        key === slug ? "" : "invisible"
                      }`}
                      key={key}
                    >
                      <div className="flex flex-wrap items-baseline gap-2.75">
                        <span className="text-[32px]/none font-black tracking-[-0.03em] sm:text-[40px]">
                          {plans[key].price}
                        </span>
                        {plans[key].was ? (
                          <span className="text-[17px] font-semibold text-white/55 line-through">
                            {plans[key].was}
                          </span>
                        ) : null}
                      </div>
                      <p className="m-0 text-[14px]/[1.5] font-semibold text-[#b9bdff]">
                        {perDay(plans[key])}
                      </p>
                      <p className="m-0 text-[13px]/[1.5] font-medium text-white/55">
                        {plans[key].validity}
                      </p>
                    </div>
                  ))}
                </div>

                <ul className="m-0 flex list-none flex-col gap-2.75 p-0 max-sm:order-last">
                  {PLUS_BENEFITS.map((benefit) => (
                    <li className="flex items-start gap-2.75" key={benefit}>
                      <span
                        aria-hidden="true"
                        className="mt-0.25 grid size-4.75 flex-none place-items-center rounded-full border border-[rgba(155,160,255,0.42)] bg-[rgba(106,112,242,0.3)] text-[#b9bdff]"
                      >
                        <Check className="size-2.75" strokeWidth={3} />
                      </span>
                      <span className="text-[14.5px]/[1.45] font-medium">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="flex flex-col gap-2.5">
                  {/* Um <a> a sério: sair da SPA para o checkout é uma
                      navegação de página inteira, e o clique tem de ser
                      reportado à mão — o listener delegado do funnel-tracker
                      não atravessa o shadow root (ver lib/tracking). */}
                  {/* Banana sobre índigo, que é o par do design system para um
                      botão neste fundo — as mesmas duas cores do
                      PLUS_CTA_ON_INDIGO dos banners, para o botão da modal ser
                      reconhecivelmente o mesmo botão. */}
                  <a
                    className="flex min-h-11 w-full items-center justify-center gap-2.5 rounded-[12px] px-6 py-4 text-center text-[16px] font-extrabold [background-color:var(--plus-accent)] [color:var(--plus-cta-hover)] transition hover:-translate-y-px hover:bg-white hover:[color:var(--plus-cta)]"
                    data-funnel-click-element={cta.element}
                    data-funnel-click-location={cta.location}
                    data-funnel-click-type={cta.type}
                    data-google-interstitial="false"
                    href={href}
                    onClick={() => trackModalClick(cta)}
                  >
                    <span className="sm:hidden">{plan.ctaShort}</span>
                    <span className="max-sm:hidden">{plan.cta}</span>
                  </a>
                  <p className="m-0 flex items-center justify-center gap-1.75 text-center text-[12.5px]/[1.4] font-medium text-white/55">
                    <ShieldCheck aria-hidden="true" className="size-3.75" />
                    {CONVERSION_GUARANTEE}
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Os pontos dizem que há um segundo cartão — sem eles o carril não se
              anuncia e o grátis fica por descobrir. Clicáveis, para quem não
              quer deslizar; escondidos a partir de lg, onde os dois já se veem.

              aria-hidden: o carril é scroll normal e os dois cartões estão no
              DOM e na ordem de leitura, por isso um leitor de ecrã chega ao
              grátis sem passar por aqui. */}
          <div aria-hidden="true" className="flex justify-center gap-2 lg:hidden">
            {[0, 1].map((index) => (
              <button
                className={`h-1.75 cursor-pointer rounded-full border-0 transition-all ${
                  card === index ? "w-6.5 bg-[#f4f4ff]" : "w-1.75 bg-white/30"
                }`}
                key={index}
                onClick={() => {
                  const rail = railRef.current;

                  if (rail) {
                    rail.scrollTo({
                      left: index * rail.clientWidth,
                      behavior: "smooth",
                    });
                  }
                }}
                tabIndex={-1}
                type="button"
              />
            ))}
          </div>

          {/* A prova, com a nota que a sustenta a um toque de distância. O ⓘ
              tem 18px de desenho e ~44px de alvo, pelo ::after do CSS abaixo —
              inset negativo, que é como o resto do produto o faz. */}
          <div className="relative flex flex-col items-center gap-1.5 text-center">
            <p className="m-0 text-[14px]/[1.5] font-medium text-white/72">
              {CONVERSION_PROOF.before}
              <b className="font-black text-[#16f5a5]">
                {CONVERSION_PROOF.value}
              </b>
              {CONVERSION_PROOF.after}
              <button
                aria-describedby="lm-proof-note"
                aria-expanded={noteOpen}
                aria-label={CONVERSION_PROOF.noteLabel}
                className="relative ml-1.25 inline-grid size-4.5 cursor-pointer place-items-center rounded-full border border-[rgba(155,160,255,0.4)] bg-white/10 align-middle font-mono text-[11px]/none font-bold text-[#b9bdff] transition after:absolute after:-inset-3.25 after:content-[''] hover:bg-[rgba(155,160,255,0.28)] hover:text-[#f4f4ff] aria-expanded:bg-[rgba(155,160,255,0.28)] aria-expanded:text-[#f4f4ff]"
                onClick={() => setNoteOpen((open) => !open)}
                ref={noteButtonRef}
                type="button"
              >
                i
              </button>
            </p>

            <div
              className={`absolute bottom-[calc(100%+12px)] left-1/2 z-8 w-[min(300px,100%)] -translate-x-1/2 rounded-[12px] border border-[rgba(155,160,255,0.34)] bg-[#191636] px-3.5 py-2.75 text-center text-[12.5px]/[1.5] font-medium text-white/82 shadow-[0_12px_32px_rgba(0,0,0,0.5)] transition after:absolute after:top-full after:left-1/2 after:-ml-1.5 after:border-6 after:border-transparent after:border-t-[#191636] after:content-[''] ${
                noteOpen
                  ? "visible translate-y-0 opacity-100"
                  : "invisible translate-y-1 opacity-0"
              }`}
              id="lm-proof-note"
              ref={noteRef}
              role="tooltip"
            >
              {CONVERSION_PROOF.note}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
